import { useEffect, useState } from "react";
import axios from "axios";
import { Calendar, Clock, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { API } from "@/contexts/AuthContext";

/**
 * MOT Slot Summary Card — compact, read-mostly panel surfaced on the
 * locked/handoff state of an MOT-family intake. Lives at the top of the
 * right rail so the FOH can see the operational slot + anticipated
 * completion time WITHOUT scrolling down through the long handoff page.
 *
 * Why this exists:
 *   - On the final intake screen the Advisor Coach and Booking Intent
 *     card were repeating wording / dates the FOH had already locked in
 *     the previous step. That duplication created friction (FOH had to
 *     scroll past stale UI to reach the workshop CTA).
 *   - The two pieces of info that ARE still useful on the handoff screen
 *     are the slot time and the anticipated completion time — that's
 *     what the workshop and customer need to align on.
 *
 * The card is read-only by default — edits happen in the slot strip
 * beside the "Send to workshop" CTA (InspectionRecommendationCard).
 */
export default function MOTSlotSummaryCard({ sessionId }) {
  const [loading, setLoading] = useState(true);
  const [slotTime, setSlotTime] = useState(null);   // ISO datetime-local
  const [duration, setDuration] = useState(45);     // minutes — sensible default
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/intake/sessions/${sessionId}/booking-intent`,
        );
        if (cancelled) return;
        const intent = r.data.booking_intent || {};
        setSlotTime(intent.mot_slot_time || null);
        if (Number.isFinite(intent.mot_slot_duration_minutes)) {
          setDuration(intent.mot_slot_duration_minutes);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || "Could not load slot");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Anticipated completion = slot_time + duration. Format locally so the
  // FOH sees the customer's clock, not UTC.
  const completionTime = slotTime
    ? addMinutes(slotTime, Number(duration) || 0)
    : null;

  if (loading) {
    return (
      <section
        className="rounded-lg border border-teal-100 bg-teal-50/50 px-5 py-4"
        data-testid="mot-slot-summary-loading"
      >
        <p className="text-[12px] uppercase tracking-[0.18em] text-teal-700 font-semibold flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading slot…
        </p>
      </section>
    );
  }

  if (!slotTime) {
    // No slot locked yet — render a gentle nudge so the FOH knows to use
    // the strip beside the Send-to-workshop CTA.
    return (
      <section
        className="rounded-lg border border-amber-200 bg-amber-50/60 px-5 py-4"
        data-testid="mot-slot-summary-empty"
      >
        <p className="text-[12px] uppercase tracking-[0.18em] text-amber-700 font-semibold flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5" />
          MOT slot not set yet
        </p>
        <p className="mt-1.5 text-[12.5px] text-amber-900/85 leading-snug">
          Set the slot time + duration in the strip beside the
          <span className="italic"> Send MOT job to workshop </span> button
          so the tester knows when the vehicle arrives.
        </p>
      </section>
    );
  }

  return (
    <section
      className="rounded-lg border border-teal-200 bg-gradient-to-br from-teal-50 to-white shadow-sm"
      data-testid="mot-slot-summary-card"
    >
      <header className="px-5 pt-3.5 pb-2 border-b border-teal-100">
        <p className="text-[12px] uppercase tracking-[0.18em] text-teal-700 font-semibold flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5" />
          MOT slot locked
        </p>
      </header>
      <div className="px-5 py-4 space-y-3">
        <SummaryRow
          icon={<Calendar className="w-4 h-4 text-teal-600" />}
          label="Slot"
          value={formatFriendly(slotTime)}
          testid="mot-slot-summary-slot"
        />
        <SummaryRow
          icon={<Clock className="w-4 h-4 text-teal-600" />}
          label="Anticipated completion"
          value={completionTime ? formatFriendly(completionTime) : "—"}
          subValue={`approx. ${duration} min test`}
          testid="mot-slot-summary-completion"
        />
        {error && (
          <p
            className="text-[11.5px] text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1"
            data-testid="mot-slot-summary-error"
          >
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Local helpers — kept inline since this is a small leaf component.
// ---------------------------------------------------------------------------

function SummaryRow({ icon, label, value, subValue, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <div className="mt-0.5 flex-shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold leading-tight">
          {label}
        </p>
        <p className="text-[15px] text-slate-900 font-semibold leading-snug truncate">
          {value}
        </p>
        {subValue && (
          <p className="text-[11.5px] text-slate-500 italic leading-snug">
            {subValue}
          </p>
        )}
      </div>
    </div>
  );
}

function addMinutes(isoLocal, minutes) {
  // The booking-intent endpoint stores slot as a datetime-local string
  // (e.g. "2026-05-20T08:30"). Round-trip via Date — safe because the
  // browser will treat it as the user's local timezone (which matches
  // how it was captured).
  try {
    const dt = new Date(isoLocal);
    if (Number.isNaN(dt.valueOf())) return null;
    dt.setMinutes(dt.getMinutes() + (Number(minutes) || 0));
    return dt.toISOString();
  } catch {
    return null;
  }
}

function formatFriendly(isoLike) {
  try {
    const dt = new Date(isoLike);
    if (Number.isNaN(dt.valueOf())) return isoLike;
    return dt.toLocaleString("en-GB", {
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(isoLike || "");
  }
}
